import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/Layout/PageHeader';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Sparkle, ArrowsClockwise, TrendUp, TrendDown, WarningCircle, Lightbulb, Target,
  Buildings, Stethoscope, Package, UsersThree, CurrencyInr, HandHeart,
} from '@phosphor-icons/react';

const SCOPES = [
  { key: 'revenue', label: 'Revenue', icon: CurrencyInr, color: 'emerald' },
  { key: 'clinics', label: 'Clinics', icon: Buildings, color: 'blue' },
  { key: 'audiologists', label: 'Audiologists', icon: Stethoscope, color: 'purple' },
  { key: 'items', label: 'Items', icon: Package, color: 'amber' },
  { key: 'partners', label: 'Partners', icon: HandHeart, color: 'rose' },
  { key: 'customers', label: 'Customers', icon: UsersThree, color: 'indigo' },
];

const ACCENT = {
  emerald: 'border-emerald-300 bg-emerald-50',
  blue: 'border-blue-300 bg-blue-50',
  purple: 'border-purple-300 bg-purple-50',
  amber: 'border-amber-300 bg-amber-50',
  rose: 'border-rose-300 bg-rose-50',
  indigo: 'border-indigo-300 bg-indigo-50',
};

const SEV = {
  high: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  low: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
};

const PRI = {
  high: { bg: 'bg-red-100', text: 'text-red-700' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-700' },
  low: { bg: 'bg-blue-100', text: 'text-blue-700' },
};

function TrendIcon({ trend }) {
  if (trend === 'up') return <TrendUp size={14} weight="bold" className="text-emerald-600" />;
  if (trend === 'down') return <TrendDown size={14} weight="bold" className="text-red-600" />;
  return <span className="text-[#64748B]">—</span>;
}

export default function AIInsightsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [scope, setScope] = useState(searchParams.get('scope') || 'revenue');
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const { data: resp } = await api.get(`/ai-insights/${scope}`, { params: { days, refresh } });
      setData(resp);
    } catch (e) {
      toast.error('Failed to load insights');
    } finally { setLoading(false); setRefreshing(false); }
  }, [scope, days]);
  useEffect(() => { load(false); }, [load]);

  const ins = data?.insights || {};
  const active = SCOPES.find(s => s.key === scope) || SCOPES[0];

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="ai-insights-page">
      <PageHeader
        title="AI Insights"
        subtitle="Claude-powered business intelligence — alerts, opportunities and recommendations across every dimension"
      />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Tabs value={scope} onValueChange={(v) => { setScope(v); setSearchParams({ scope: v }); }} className="flex-1">
          <TabsList className="bg-[#F8FAFC]">
            {SCOPES.map(s => (
              <TabsTrigger key={s.key} value={s.key} data-testid={`ai-scope-${s.key}`} className="data-[state=active]:bg-white">
                <s.icon size={14} className="mr-1.5" /> {s.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2 ml-auto">
          <select value={days} onChange={e => setDays(Number(e.target.value))} className="h-9 rounded border border-[#E2E8F0] px-2 text-xs" data-testid="ai-period-select">
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last 12 months</option>
          </select>
          <Button size="sm" variant="outline" onClick={() => load(true)} disabled={refreshing} data-testid="ai-regenerate">
            <Sparkle size={14} className={`mr-1 ${refreshing ? 'animate-pulse' : ''}`} />
            {refreshing ? 'Generating...' : 'Regenerate'}
          </Button>
        </div>
      </div>

      {/* Cache status */}
      {data && (
        <div className="mt-3 text-xs text-[#64748B] flex items-center gap-2" data-testid="ai-cache-status">
          {data.cached
            ? <>Cached · generated {data.age_minutes} min ago · click Regenerate for fresh analysis</>
            : <span className="text-emerald-700">✓ Fresh — generated just now</span>}
        </div>
      )}

      {loading ? (
        <div className="mt-8 bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center" data-testid="ai-loading">
          <Sparkle size={32} weight="fill" className="mx-auto text-[#0F4C81] animate-pulse mb-3" />
          <p className="text-sm font-medium text-[#0F172A]">Claude is analyzing your {active.label.toLowerCase()} data...</p>
          <p className="text-xs text-[#64748B] mt-1">This typically takes 20–60 seconds for fresh analysis.</p>
        </div>
      ) : !data ? null : (
        <div className="mt-6 space-y-5" data-testid="ai-content">
          {/* Headline */}
          {ins.headline && (
            <div className={`border-l-4 rounded-r-lg p-4 ${ACCENT[active.color]}`} data-testid="ai-headline">
              <div className="flex items-start gap-2">
                <Sparkle size={18} weight="fill" className="text-[#0F4C81] mt-0.5 flex-shrink-0" />
                <p className="text-sm font-medium text-[#0F172A] leading-relaxed">{ins.headline}</p>
              </div>
            </div>
          )}

          {/* Highlights */}
          {ins.highlights?.length > 0 && (
            <Section title="Key Highlights" icon={TrendUp}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ai-highlights">
                {ins.highlights.map((h, i) => (
                  <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-[10px] uppercase tracking-wide text-[#64748B] font-medium">{h.title}</p>
                      <TrendIcon trend={h.trend} />
                    </div>
                    <p className="text-lg font-bold text-[#0F172A] mb-1">{h.value}</p>
                    <p className="text-xs text-[#64748B] leading-relaxed">{h.detail}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Alerts + Opportunities side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {ins.alerts?.length > 0 && (
              <Section title="Alerts & Risks" icon={WarningCircle} accent="text-red-600">
                <div className="space-y-2" data-testid="ai-alerts">
                  {ins.alerts.map((a, i) => {
                    const sev = SEV[a.severity] || SEV.low;
                    return (
                      <div key={i} className={`border rounded-lg p-3 ${sev.bg} ${sev.border}`}>
                        <div className="flex items-start gap-2">
                          <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${sev.dot}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2 mb-0.5">
                              <p className={`text-sm font-semibold ${sev.text}`}>{a.title}</p>
                              <span className={`text-[10px] uppercase tracking-wider font-bold ${sev.text}`}>{a.severity}</span>
                            </div>
                            <p className="text-xs text-[#0F172A] leading-relaxed">{a.detail}</p>
                            {a.entity && <p className="text-[10px] text-[#64748B] mt-1">→ {a.entity}</p>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}

            {ins.opportunities?.length > 0 && (
              <Section title="Opportunities" icon={Target} accent="text-emerald-700">
                <div className="space-y-2" data-testid="ai-opportunities">
                  {ins.opportunities.map((o, i) => (
                    <div key={i} className="border border-emerald-200 bg-emerald-50 rounded-lg p-3">
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <p className="text-sm font-semibold text-emerald-700">{o.title}</p>
                        {o.potential_value && <span className="text-xs font-bold text-emerald-700">{o.potential_value}</span>}
                      </div>
                      <p className="text-xs text-[#0F172A] leading-relaxed">{o.detail}</p>
                      {o.entity && <p className="text-[10px] text-[#64748B] mt-1">→ {o.entity}</p>}
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </div>

          {/* Recommendations */}
          {ins.recommendations?.length > 0 && (
            <Section title="Recommendations" icon={Lightbulb}>
              <div className="space-y-2" data-testid="ai-recommendations">
                {ins.recommendations.map((r, i) => {
                  const pri = PRI[r.priority] || PRI.low;
                  return (
                    <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl p-3 flex items-start gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#0F4C81] text-white flex items-center justify-center text-xs font-bold">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${pri.bg} ${pri.text}`}>{r.priority} priority</span>
                        </div>
                        <p className="text-sm font-medium text-[#0F172A] mb-1">{r.action}</p>
                        <p className="text-xs text-[#64748B] leading-relaxed">{r.rationale}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {ins.raw && (
            <details className="bg-[#F8FAFC] rounded-lg p-3 text-xs text-[#64748B]">
              <summary className="cursor-pointer">Raw Claude output (debug)</summary>
              <pre className="mt-2 whitespace-pre-wrap">{ins.raw}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}


function Section({ title, icon: Icon, accent = 'text-[#0F4C81]', children }) {
  return (
    <div>
      <h3 className={`text-sm font-semibold mb-2 flex items-center gap-1.5 ${accent}`}>
        <Icon size={16} weight="fill" /> {title}
      </h3>
      {children}
    </div>
  );
}
